import loadPreactModule from "../../../utils/loadPreactModule";
import callIfArgumentsPass from "../../../utils/callIfArgumentsPass";
import renderPreactWithProps from "../../../utils/renderPreactWithProps";
import curry from "../../../lib/core/functional/curry";
import thunk from "../../../lib/core/functional/thunk";

import * as riverTransport from "../../../transport/river";

import loadNotification from "./notification/loader";

const USER_IS_FOLLOWED = 4;
const REPLY_COMMENT = 27;
const POST_TALK_COMMENT = 28;

const DEFAULT_EVENTS = [USER_IS_FOLLOWED, REPLY_COMMENT, POST_TALK_COMMENT];

function isUserLoggedIn(prevErr, prevUserInfo, err, userInfo) {
    if (prevErr != err) {
        return true;
    }

    return userInfo.profileSite.title !== prevUserInfo.profileSite.title;
}

function markNotificationAsRead(notification) {
    return { ...notification, isUnread: false };
}

function formatNotification(notification) {
    const {
        emitter_site_title,
        emitter_site_name,
        id,
        message_html,
        emitter_avatar,
        post_permalink,
        reply_url_param,
        created_date_html,
        created_time_html,
        event_type_id,
    } = notification;
    return {
        id: id,
        message: message_html,
        siteName: emitter_site_name,
        siteTitle: emitter_site_title,
        siteAvatar: emitter_avatar,
        permalink: [post_permalink, reply_url_param].filter(Boolean).join("?"),
        fullDate: `${created_date_html} ${created_time_html}`,
        date: created_date_html,
        eventType: event_type_id,
        isUnread: false,
    };
}

function handlePublishedPostsResponse(notifications) {
    return notifications.map(formatNotification);
}

function isUnread(notification) {
    return notification.isUnread;
}

export const renderRiverTopBar = curry(function renderRiverTopBar(
    renderModule,
    events,
    { getCounterUpdates, clearCounter, getLastPublishedPosts },
    err,
    userInfo
) {
    if (err) {
        console.error(err);
        return null;
    }
    let previousCount = -1;
    const { profileSite } = userInfo;
    const picture = profileSite.display_logo;
    const username = profileSite.title;

    function notificationIsOfAllowedType(notification) {
        return events.includes(notification.eventType);
    }

    function filterNotificationsByType(notifications) {
        return notifications.filter(notificationIsOfAllowedType);
    }

    const markAllAsRead = thunk(function markAllAsRead(notifications) {
        previousCount = 0;
        clearCounter(profileSite.id);
        renderProps(notifications.map(markNotificationAsRead));
    });

    function renderProps(notifications) {
        renderModule({
            notifications,
            picture,
            username,
            count: notifications.filter(isUnread).length,
            markAllAsRead: markAllAsRead(notifications),
        });
    }

    const markUnreadNotifications = curry(function markUnreadNotifications(
        unreadCount,
        notifications
    ) {
        return notifications.map((notification, index) => ({
            ...notification,
            isUnread: index < unreadCount,
        }));
    });

    function getNotifications(unreadCount) {
        getLastPublishedPosts(0, 20, 0)
            .then(handlePublishedPostsResponse)
            .then(filterNotificationsByType)
            .then(markUnreadNotifications(unreadCount))
            .then(renderProps);
    }

    function onNewCount(data) {
        if (data.count > previousCount) {
            previousCount = data.count;
            getNotifications(previousCount);
        }
    }

    const stopCounterUpdates = getCounterUpdates(profileSite.id, onNewCount);
    renderProps([]);
    return stopCounterUpdates;
});

const toInt = (number) => parseInt(number, 10);

const onModule = curry(function onModule(context, element, [renderer, Module]) {
    const events = element.dataset.events
        ? element.dataset.events.split(",").map(toInt)
        : [...DEFAULT_EVENTS];

    context.listenForUserInfo(
        callIfArgumentsPass(
            isUserLoggedIn,
            renderRiverTopBar(
                renderPreactWithProps(renderer, Module, element),
                events,
                riverTransport
            )
        )
    );
});

const internalLoader = () => import("./module").then((module) => module.default);

export default function renderer(context, element) {
    loadNotification(context)
        .then(([renderer, Notification]) => {
            loadPreactModule("RiverTopBar", context, { Notification }, internalLoader)
                .then(onModule(context, element))
                .catch(console.error);
        })
        .catch(console.error);
}
